// PricingPageCulqi.jsx - Página de precios optimizada para cumplir con Culqi
import React, { useState } from 'react';
import { 
  Check, X, Star, Zap, Shield, Award, Clock, 
  TrendingUp, Users, BookOpen, Target, CreditCard,
  ChevronRight, Info, Lock, Unlock
} from 'lucide-react';

const PricingPageCulqi = ({ user, onSelectPlan }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Planes disponibles
  const plans = {
    trial: {
      name: 'Plan Trial',
      price: 0,
      period: '3 días',
      description: 'Prueba gratuita para nuevos usuarios',
      color: 'gray',
      features: [
        { text: '3 días de acceso gratuito', included: true },
        { text: 'Máximo 20 preguntas por examen', included: true },
        { text: 'Acceso a todas las especialidades', included: true },
        { text: 'Estadísticas básicas', included: true },
        { text: 'Modo práctica', included: true },
        { text: 'Modo examen ilimitado', included: false },
        { text: 'Simulacros ENARM completos', included: false },
        { text: 'Acceso a nuevas funciones prioritarias', included: false }
      ],
      cta: 'Activar Trial Gratis',
      badge: null
    },
    pro_monthly: {
      name: 'Plan PRO Mensual',
      price: 39.90,
      period: 'mes',
      description: 'Acceso completo sin restricciones',
      color: 'blue',
      features: [
        { text: 'Acceso ilimitado 24/7', included: true },
        { text: 'Preguntas ilimitadas por examen', included: true },
        { text: 'Todas las especialidades médicas', included: true },
        { text: 'Estadísticas detalladas de rendimiento', included: true },
        { text: 'Todos los modos de estudio', included: true },
        { text: 'Modo examen sin límites', included: true },
        { text: 'Simulacros ENARM completos', included: true },
        { text: 'Acceso a nuevas funciones de manera prioritaria', included: true }
      ],
      cta: 'Suscribirme Ahora',
      badge: 'RECOMENDADO'
    },
    pro_quarterly: {
      name: 'Plan PRO Trimestral',
      price: 79.90,
      originalPrice: 119.70,
      period: '3 meses',
      description: 'Paga 2 meses y obtén 3',
      color: 'purple',
      features: [
        { text: 'Acceso ilimitado 24/7', included: true },
        { text: 'Preguntas ilimitadas por examen', included: true },
        { text: 'Todas las especialidades médicas', included: true },
        { text: 'Estadísticas detalladas de rendimiento', included: true },
        { text: 'Todos los modos de estudio', included: true },
        { text: 'Modo examen sin límites', included: true },
        { text: 'Simulacros ENARM completos', included: true },
        { text: 'Acceso a nuevas funciones de manera prioritaria', included: true }
      ],
      cta: 'Ahorrar con Trimestral',
      badge: 'MEJOR PRECIO',
      savings: 'Ahorra S/. 39.80'
    }
  };
  
  // Comparación de características
  const comparisonFeatures = [
    { 
      category: 'Acceso y Contenido',
      features: [
        { name: 'Duración del acceso', trial: '3 días', pro: '30 o 90 días' },
        { name: 'Preguntas por examen', trial: '20 máximo', pro: 'Ilimitadas' },
        { name: 'Especialidades médicas', trial: '15+ áreas', pro: '15+ áreas' },
        { name: 'Base de preguntas', trial: '1,200+', pro: '1,200+' },
        { name: 'Actualizaciones', trial: 'Limitadas', pro: 'Continuas' }
      ]
    },
    {
      category: 'Modos de Estudio',
      features: [
        { name: 'Modo Práctica', trial: '✓', pro: '✓' },
        { name: 'Modo Examen', trial: '✗', pro: '✓' },
        { name: 'Simulacros ENARM completos', trial: '✗', pro: '✓' },
        { name: 'Modo Maratón', trial: '✗', pro: '✓' },
        { name: 'Flashcards', trial: 'Básico', pro: 'Avanzado' }
      ]
    },
    {
      category: 'Análisis y Estadísticas',
      features: [
        { name: 'Estadísticas básicas', trial: '✓', pro: '✓' },
        { name: 'Estadísticas detalladas', trial: '✗', pro: '✓' },
        { name: 'Reporte de progreso', trial: 'Simple', pro: 'Detallado' },
        { name: 'Historial completo', trial: '✗', pro: '✓' }
      ]
    },
    {
      category: 'Soporte y Extras',
      features: [
        { name: 'Soporte por email', trial: 'Básico', pro: 'Prioritario' },
        { name: 'Acceso a nuevas funciones', trial: '✗', pro: 'Prioritario' },
        { name: 'Material de estudio', trial: '✗', pro: '✓' },
        { name: 'Garantía devolución', trial: 'N/A', pro: '7 días' }
      ]
    }
  ];
  
  // Testimonios
  const testimonials = [
    {
      name: 'Dr. Carlos Mendoza',
      role: 'Residente R1 - HNGAI',
      text: 'Gracias a MediExamPro aprobé mi ENARM en el primer intento. Las preguntas son muy similares al examen real.',
      rating: 5
    },
    {
      name: 'Dra. María Gutiérrez',
      role: 'Médico General',
      text: 'La mejor inversión para mi preparación. El análisis predictivo me ayudó a enfocarme en mis puntos débiles.',
      rating: 5
    },
    {
      name: 'Dr. Luis Vargas',
      role: 'Residente R2 - Hospital Rebagliati',
      text: 'Los simulacros son idénticos al ENARM real. Vale cada centavo de la suscripción PRO.',
      rating: 5
    }
  ];
  
  const handleSelectPlan = (planKey) => {
    setSelectedPlan(planKey);
    if (onSelectPlan) {
      onSelectPlan(planKey);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Planes y Precios Transparentes
          </h1>
          <p className="text-xl text-blue-100 mb-8">
            Sin letras pequeñas, sin costos ocultos. Elige el plan perfecto para ti.
          </p>
          
          {/* Garantías */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-400" />
              <span>Pago 100% Seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-400" />
              <span>Activación Inmediata</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-400" />
              <span>Garantía 7 días</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Planes Cards */}
      <div className="max-w-7xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([key, plan]) => (
            <div 
              key={key}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-105 ${
                plan.badge ? 'ring-4 ring-blue-500 ring-opacity-50' : ''
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center py-2 text-sm font-bold">
                  {plan.badge}
                </div>
              )}
              
              {/* Plan Header */}
              <div className="p-6 text-center border-b">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                
                {/* Precio */}
                <div className="mb-3">
                  {plan.originalPrice && (
                    <div className="text-gray-400 line-through text-lg">
                      S/. {plan.originalPrice}
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      S/. {plan.price}
                    </span>
                    <span className="text-gray-600">/{plan.period}</span>
                  </div>
                  {plan.savings && (
                    <div className="mt-2 text-green-600 font-medium">
                      {plan.savings}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-4">Incluye IGV</div>
                
                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(key)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                    key === 'trial' 
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                  }`}
                >
                  {plan.cta}
                  <ChevronRight className="inline-block ml-2 h-4 w-4" />
                </button>

                {/* Enlaces rápidos legales para Culqi */}
                {key !== 'trial' && (
                  <div className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-3">
                    <a href="/terminos" className="hover:text-gray-700 underline">Términos</a>
                    <span>•</span>
                    <a href="/devoluciones" className="hover:text-gray-700 underline">Devoluciones</a>
                  </div>
                )}
              </div>
              
              {/* Features List */}
              <div className="p-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${
                        feature.included ? 'text-gray-700' : 'text-gray-400'
                      }`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Tabla Comparativa Detallada */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">
          Comparación Detallada de Planes
        </h2>
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-4 font-semibold text-gray-900">
                    Características
                  </th>
                  <th className="text-center p-4 font-semibold text-gray-900">
                    Plan Trial
                  </th>
                  <th className="text-center p-4 font-semibold text-gray-900 bg-blue-50">
                    Plan PRO
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((category, catIndex) => (
                  <React.Fragment key={catIndex}>
                    <tr className="bg-gray-100">
                      <td colSpan="3" className="p-3 font-semibold text-gray-700">
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature, featIndex) => (
                      <tr key={featIndex} className="border-b">
                        <td className="p-4 text-gray-700">{feature.name}</td>
                        <td className="p-4 text-center text-gray-600">
                          {feature.trial}
                        </td>
                        <td className="p-4 text-center font-medium text-gray-900 bg-blue-50/30">
                          {feature.pro}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Testimonios */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Lo que dicen nuestros usuarios
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* FAQ Rápido */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">
          Preguntas Frecuentes sobre Precios
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-6 shadow">
            <h3 className="font-semibold text-lg mb-2">
              ¿Puedo cancelar mi suscripción en cualquier momento?
            </h3>
            <p className="text-gray-700">
              Sí, puedes cancelar tu suscripción en cualquier momento desde tu panel de usuario. 
              No hay penalizaciones ni compromisos de permanencia.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow">
            <h3 className="font-semibold text-lg mb-2">
              ¿Qué métodos de pago aceptan?
            </h3>
            <p className="text-gray-700">
              Aceptamos todas las tarjetas de crédito y débito (Visa, Mastercard, American Express, Diners Club), 
              además de Yape y Plin. Todos los pagos son procesados de forma segura por Culqi.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow">
            <h3 className="font-semibold text-lg mb-2">
              ¿Hay garantía de devolución?
            </h3>
            <p className="text-gray-700">
              Sí, ofrecemos una garantía de satisfacción de 7 días. Si no estás satisfecho con el servicio, 
              puedes solicitar la devolución completa de tu dinero dentro de los primeros 7 días.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow">
            <h3 className="font-semibold text-lg mb-2">
              ¿El precio incluye IGV?
            </h3>
            <p className="text-gray-700">
              Sí, todos nuestros precios incluyen IGV. El precio que ves es el precio final que pagarás.
            </p>
          </div>
        </div>
      </div>
      
      {/* CTA Final */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Listo para aprobar tu ENARM?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Únete a más de 5,000 médicos que confían en MediExamPro
          </p>
          <button
            onClick={() => handleSelectPlan('pro_monthly')}
            className="bg-white text-blue-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105"
          >
            Comenzar Ahora - 3 Días Gratis
          </button>
          <p className="mt-4 text-sm text-blue-200">
            No se requiere tarjeta de crédito para el trial
          </p>
        </div>
      </div>
      
      {/* Footer de métodos de pago */}
      <div className="bg-white py-8 border-t">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4 mb-4">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-gray-700">Pago 100% Seguro con Culqi</span>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <span className="px-3 py-1 bg-gray-100 rounded">Visa</span>
            <span className="px-3 py-1 bg-gray-100 rounded">Mastercard</span>
            <span className="px-3 py-1 bg-gray-100 rounded">American Express</span>
            <span className="px-3 py-1 bg-gray-100 rounded">Diners Club</span>
            <span className="px-3 py-1 bg-gray-100 rounded">Yape</span>
            <span className="px-3 py-1 bg-gray-100 rounded">Plin</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPageCulqi;
